import { useState } from 'react';
import { Button, Group, Select, Skeleton, Stack, Table, Text } from '@mantine/core';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { Video, Visibility } from '@nelnela/contracts';
import { api, ApiError } from '../../lib/api';
import { formatBytes } from '../../lib/text';
import { useStudy } from '../../app/study';

type VisFilter = 'all' | Visibility;
type AvailFilter = 'all' | 'yes' | 'no';

export const CURATOR_VIDEOS_KEY = ['curator', 'videos'] as const;

/**
 * F15 curator → Videos: every row from `video` (all visibilities; the only list that shows
 * unavailable ones). Filters: visibility · available. Per row: title (→ Watch) · channel ·
 * visibility + Share/Unshare (`videoShare`; a 409 from the DB trigger — channel not
 * allowlisted / no transcript — shows its detail inline) · available ✓/✗ + "Check now"
 * (`videoAvailabilityCheck` → the job's message) · lines · items + "Re-tag items"
 * (`transcriptResolve` → the job's message) · created.
 *
 * M4c: an upload row reads "upload · 1.2 GB" in the channel column and carries NO Share control.
 * It has no channel — that is what makes it unshareable — so `fn_video_shared_guard` would refuse
 * 'shared', and a button whose only possible outcome is a 409 is a lie about what is on offer.
 * Nothing about the stored object itself belongs here either (no key, no stream link, no size
 * breakdown): this table lists the `video` row, and the media is the owner's alone.
 */
export function CuratorVideos() {
  const qc = useQueryClient();
  const meId = useStudy().me.learner.id;
  const [vis, setVis] = useState<VisFilter>('all');
  const [avail, setAvail] = useState<AvailFilter>('all');
  const filters = {
    visibility: vis === 'all' ? undefined : vis,
    available: avail === 'all' ? undefined : avail === 'yes',
  };
  const q = useInfiniteQuery({
    queryKey: [...CURATOR_VIDEOS_KEY, filters],
    queryFn: ({ pageParam }) => api.curatorVideos({ ...filters, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  // per-row lines: the 409 detail after Share, or the check / re-tag job's message
  const [lines, setLines] = useState<Record<string, string>>({});
  const setLine = (id: string, msg: string) => setLines((m) => ({ ...m, [id]: msg }));
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: CURATOR_VIDEOS_KEY });
    void qc.invalidateQueries({ queryKey: ['videos'] });
  };
  const share = useMutation({
    mutationFn: (v: { id: string; visibility: Visibility }) => api.videoShare(v.id, v.visibility),
    onSuccess: (_d, v) => {
      setLines((m) => {
        const { [v.id]: _drop, ...rest } = m;
        return rest;
      });
      invalidate();
    },
    onError: (err, v) =>
      setLine(
        v.id,
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title)
          : 'Couldn’t change visibility.',
      ),
  });
  const check = useMutation({
    mutationFn: (id: string) => api.videoAvailabilityCheck(id),
    onSuccess: (job, id) => {
      setLine(id, job.message ?? job.status);
      invalidate();
    },
    onError: (err, id) =>
      setLine(
        id,
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Check failed.',
      ),
  });
  // The resolver runs itself after `videoCreate` and after a cleanup finalize; this re-runs it
  // over one transcript. Keyed by video id (the row) but posted by transcript id (the endpoint).
  const resolve = useMutation({
    mutationFn: (v: { id: string; transcriptId: string }) => api.transcriptResolve(v.transcriptId),
    onSuccess: (job, v) => {
      // a freshly queued job has no message yet — its status is the only thing to report
      setLine(v.id, job.message ?? `Resolver ${job.status}.`);
      invalidate();
    },
    onError: (err, v) =>
      setLine(
        v.id,
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Re-tag failed.',
      ),
  });
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <Stack gap="sm">
      <Group gap="sm">
        <Select
          size="xs"
          w={150}
          label="Visibility"
          value={vis}
          onChange={(v) => setVis((v as VisFilter) ?? 'all')}
          data={[
            { value: 'all', label: 'All' },
            { value: 'shared', label: 'Shared' },
            { value: 'owner', label: 'Private (owner)' },
          ]}
          allowDeselect={false}
        />
        <Select
          size="xs"
          w={150}
          label="Available"
          value={avail}
          onChange={(v) => setAvail((v as AvailFilter) ?? 'all')}
          data={[
            { value: 'all', label: 'All' },
            { value: 'yes', label: 'Available' },
            { value: 'no', label: 'Unavailable' },
          ]}
          allowDeselect={false}
        />
      </Group>
      <Text fz="xs" c="dimmed">
        New videos are tagged automatically. “Re-tag items” re-runs the resolver over one transcript
        — for videos added before the tracking engine, and after seeding new patterns.
      </Text>
      {q.isPending && <Skeleton height={120} radius="md" />}
      {q.error && (
        <Text c="dimmed" fz="sm">
          {q.error instanceof ApiError && q.error.problem.status === 403
            ? 'Curator tools are for curators.'
            : 'Couldn’t load videos.'}
        </Text>
      )}
      {q.data && (
        <Table.ScrollContainer minWidth={1000}>
          <Table striped highlightOnHover withTableBorder fz="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Title</Table.Th>
                <Table.Th>Channel</Table.Th>
                <Table.Th>Visibility</Table.Th>
                <Table.Th>Available</Table.Th>
                <Table.Th ta="right">Lines</Table.Th>
                <Table.Th>Items</Table.Th>
                <Table.Th>Created</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed" fz="sm">
                      No videos match.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {rows.map((v) => (
                <VideoRow
                  key={v.id}
                  v={v}
                  meId={meId}
                  line={lines[v.id]}
                  sharing={share.isPending && share.variables?.id === v.id}
                  checking={check.isPending && check.variables === v.id}
                  resolving={resolve.isPending && resolve.variables?.id === v.id}
                  onShare={(visibility) => share.mutate({ id: v.id, visibility })}
                  onCheck={() => check.mutate(v.id)}
                  onResolve={(transcriptId) => resolve.mutate({ id: v.id, transcriptId })}
                />
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {q.hasNextPage && (
        <Button
          variant="default"
          size="xs"
          w="fit-content"
          onClick={() => q.fetchNextPage()}
          loading={q.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
    </Stack>
  );
}

function VideoRow({
  v,
  meId,
  line,
  sharing,
  checking,
  resolving,
  onShare,
  onCheck,
  onResolve,
}: {
  v: Video;
  /** the current learner — `videoGet` only opens shared videos and the learner's own */
  meId: string;
  line: string | undefined;
  sharing: boolean;
  checking: boolean;
  resolving: boolean;
  onShare: (visibility: Visibility) => void;
  onCheck: () => void;
  onResolve: (transcriptId: string) => void;
}) {
  const shared = v.visibility === 'shared';
  const upload = v.provider === 'upload';
  // nothing to resolve without a transcript — the endpoint 404s on one that isn't there
  const transcriptId = v.transcript?.id ?? null;
  // Watch (`GET /api/videos/:id`) answers 404 for another learner's private video and for an
  // unavailable one (the visible_video view filters it) — no curator exception, so don't link.
  const viewable = (shared || v.ownerId === meId) && v.available;
  const notViewable = !v.available ? 'unavailable' : 'private · not viewable';
  return (
    <Table.Tr>
      <Table.Td style={{ maxWidth: 320 }}>
        <Text fz="sm" truncate lang="ka">
          {viewable ? (
            <Link to={`/watch/${v.id}`} style={{ color: 'var(--nn-link)', textDecoration: 'none' }}>
              {v.title ?? v.externalId}
            </Link>
          ) : (
            <>
              {v.title ?? v.externalId}
              <Text component="span" fz="xs" c="dimmed" ml={6}>
                {notViewable}
              </Text>
            </>
          )}
        </Text>
        <Text ff="monospace" fz="xs" c="dimmed">
          {v.externalId}
        </Text>
        {line && (
          <Text fz="xs" c="dimmed" mt={2}>
            {line}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {upload ? (
          // not lang="ka": this is the file's provenance, not a Georgian channel name
          <Text fz="sm">
            {v.mediaBytes != null ? `upload · ${formatBytes(v.mediaBytes)}` : 'upload'}
          </Text>
        ) : (
          <>
            <Text fz="sm" lang="ka">
              {v.channel?.name ?? '—'}
            </Text>
            {v.channel && !v.channel.allowlisted && (
              <Text fz="xs" c="dimmed">
                not allowlisted
              </Text>
            )}
          </>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          <Text fz="sm">{shared ? 'shared' : 'owner'}</Text>
          {/* no Share for an upload: no channel → the trigger refuses 'shared', always */}
          {!upload && (
            <Button
              size="compact-xs"
              variant="default"
              loading={sharing}
              onClick={() => onShare(shared ? 'owner' : 'shared')}
            >
              {shared ? 'Unshare' : 'Share'}
            </Button>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          <Text fz="sm" aria-label={v.available ? 'available' : 'unavailable'}>
            {v.available ? '✓' : '✗'}
          </Text>
          <Button size="compact-xs" variant="subtle" loading={checking} onClick={onCheck}>
            Check now
          </Button>
        </Group>
      </Table.Td>
      <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {v.transcript ? v.transcript.lineCount : '—'}
      </Table.Td>
      <Table.Td>
        {transcriptId ? (
          <Button
            size="compact-xs"
            variant="subtle"
            loading={resolving}
            onClick={() => onResolve(transcriptId)}
          >
            Re-tag items
          </Button>
        ) : (
          <Text fz="sm" c="dimmed">
            —
          </Text>
        )}
      </Table.Td>
      <Table.Td style={{ whiteSpace: 'nowrap' }}>
        <Text fz="xs" c="dimmed">
          {new Date(v.createdAt).toLocaleDateString()}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
}
